import { aboutInfo, faqList } from '@/mocks/videos';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

export default function AboutPage() {
  const pageTitle = '关于我们 - 嫩草视频 | 品牌介绍与联系方式';
  const pageDesc = '嫩草视频品牌介绍，了解我们的平台优势与服务理念。高清流畅在线视频平台，电影电视剧资源丰富，手机网页版即点即看。提供联系方式与常见问题解答。';

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '嫩草视频',
    url: 'https://classwall.cn',
    description: '在线视频内容聚合平台',
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
      hoursAvailable: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '09:00',
          closes: '21:00',
        },
      ],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '互联网科技园区A座',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const AboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: pageTitle,
    description: pageDesc,
    url: 'https://classwall.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '嫩草视频',
      url: 'https://classwall.cn',
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords="嫩草视频,关于我们,品牌介绍,联系方式,在线视频平台,手机看"
        canonicalUrl="https://classwall.cn/about"
        ogTitle={pageTitle}
        ogDescription={pageDesc}
        ogType="website"
        ogUrl="https://classwall.cn/about"
        schema={AboutSchema}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: '关于我们' },
        ]}
      />

      <main className="w-full">
        <BrandSection />
        <FeaturesSection />
        <TestimonialsSection />
        <FAQSection />
        <ContactSection />
      </main>
    </>
  );
}

function BrandSection() {
  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
      <div className="max-w-[900px] mx-auto text-center">
        <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-6">
          关于嫩草视频
        </h1>
        <div className="w-16 h-1 bg-primary-500 mx-auto mb-8 rounded-full"></div>
        <p className="text-foreground-600 leading-relaxed text-base md:text-lg whitespace-pre-line max-w-[700px] mx-auto">
          {aboutInfo.introduction}
        </p>
      </div>
    </section>
  );
}

function FeaturesSection() {
  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
            我们的优势
          </h2>
          <p className="text-foreground-500 text-sm">为什么选择嫩草视频</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {aboutInfo.features.map((feature) => (
            <div
              key={feature.title}
              className="p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <i className={`${feature.icon} text-xl text-primary-600`}></i>
              </div>
              <h3 className="text-lg font-heading text-foreground-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
            用户口碑
          </h2>
          <p className="text-foreground-500 text-sm">听听用户怎么说</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {aboutInfo.testimonials.map((testimonial, idx) => (
            <div
              key={idx}
              className="p-5 rounded-xl bg-background-100 border border-background-200/70"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <span className="text-accent-700 font-medium text-sm">{testimonial.avatar}</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">{testimonial.name}</div>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                "{testimonial.content}"
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
      <div className="max-w-[800px] mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
            常见问题
          </h2>
          <p className="text-foreground-500 text-sm">快速解答你的疑惑</p>
        </div>

        <div className="space-y-3">
          {faqList.map((faq, index) => (
            <div
              key={index}
              className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                <i
                  className={`text-foreground-400 text-lg transition-transform flex-shrink-0 ${
                    openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                  }`}
                ></i>
              </button>
              {openIndex === index && (
                <div className="px-5 pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
      <div className="max-w-[900px] mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
            联系我们
          </h2>
          <p className="text-foreground-500 text-sm">有任何问题欢迎随时联系</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-mail-line text-xl text-primary-600"></i>
            </div>
            <h4 className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</h4>
            <p className="text-xs text-foreground-500">{aboutInfo.contact.email}</p>
          </div>

          <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-wechat-line text-xl text-accent-600"></i>
            </div>
            <h4 className="text-sm font-medium text-foreground-800 mb-1">微信公众号</h4>
            <p className="text-xs text-foreground-500">{aboutInfo.contact.wechat}</p>
          </div>

          <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-secondary-300 flex items-center justify-center">
              <i className="ri-qq-line text-xl text-secondary-700"></i>
            </div>
            <h4 className="text-sm font-medium text-foreground-800 mb-1">QQ群</h4>
            <p className="text-xs text-foreground-500">{aboutInfo.contact.qq}</p>
          </div>

          <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-time-line text-xl text-primary-600"></i>
            </div>
            <h4 className="text-sm font-medium text-foreground-800 mb-1">工作时间</h4>
            <p className="text-xs text-foreground-500">{aboutInfo.contact.workingHours}</p>
          </div>
        </div>
      </div>
    </section>
  );
}